import Checkbox from '@/components/ui/Form/Checkbox';
import TextInput from '../../Form/TextInput';
import SelectSubPanel from '../../Panels/SelectSubPanel';
import React, { useCallback, useEffect, useMemo } from 'react';
import { invalidZipFormat } from '@/utils/validations';
import Button from '../../Buttons/Button';
import { SelectorItem } from '../../Filter/types';
import { Address } from '@/@types/client-api';
import EditPanelProvider from '../../Panels/EditPanel/context';
import { Root } from '@radix-ui/react-form';

const zipErrors = [invalidZipFormat()];

type AddressFormProps = {
  isInlineForm?: boolean;
  isOpen?: boolean;
  attention?: string;
  setAttention: (value: string) => void;
  typeRef?: React.RefObject<HTMLButtonElement>;
  addressCategories?: SelectorItem[];
  addressCategory?: string;
  setAddressCategory: (value: string) => void;
  company?: string;
  setCompany: (value: string) => void;
  address1?: string;
  setAddress1: (value: string) => void;
  addressRef?: React.RefObject<HTMLInputElement>;
  address2?: string;
  setAddress2: (value: string) => void;
  city?: string;
  setCity: (value: string) => void;
  cityRef?: React.RefObject<HTMLInputElement>;
  addressStates?: SelectorItem[];
  addressState?: string;
  setAddressState: (value: string) => void;
  stateRef?: React.RefObject<HTMLButtonElement>;
  isUnitedStatesOrCanada?: boolean;
  isUnitedStates?: boolean;
  addressCountries?: SelectorItem[];
  addressCountry?: string;
  setAddressCountry: (value: string) => void;
  zip?: string;
  setZip: (value: string) => void;
  zipRef?: React.RefObject<HTMLInputElement>;
  billingAddress?: boolean;
  setBillingAddress: React.Dispatch<React.SetStateAction<boolean>>;
  shippingAddress?: boolean;
  setShippingAddress: React.Dispatch<React.SetStateAction<boolean>>;
  isProcessing?: boolean;
  isInitialAddress?: boolean;
  validationFailed?: boolean;
  handleOnSave?: () => void;
  handleOnClose?: (open: boolean) => void;
  data?: Address;
  type?: 'billing' | 'shipping';
};

const AddressForm: React.FC<AddressFormProps> = ({
  isInlineForm,
  isOpen,
  attention,
  setAttention,
  typeRef,
  addressCategories,
  addressCategory,
  setAddressCategory,
  company,
  setCompany,
  address1,
  setAddress1,
  addressRef,
  address2,
  setAddress2,
  city,
  setCity,
  cityRef,
  addressStates,
  addressState,
  setAddressState,
  stateRef,
  isUnitedStatesOrCanada,
  isUnitedStates,
  addressCountries,
  addressCountry,
  setAddressCountry,
  zip,
  setZip,
  zipRef,
  billingAddress,
  setBillingAddress,
  shippingAddress,
  setShippingAddress,
  isProcessing,
  isInitialAddress,
  validationFailed,
  handleOnSave,
  handleOnClose,
  data,
  type
}) => {
  const toggleBillingAddress = useCallback(
    () => setBillingAddress(state => !state),
    [setBillingAddress]
  );
  const toggleShippingAddress = useCallback(
    () => setShippingAddress(state => !state),
    [setShippingAddress]
  );

  useEffect(() => {
    if (isOpen || isInlineForm) {
      setAddressCategory(data?.addressCategory_SK?.toString() ?? '');
      setAttention(data?.attention ?? '');
      setCompany(data?.company ?? '');
      setAddress1(data?.address1 ?? '');
      setAddress2(data?.address2 ?? '');
      setCity(data?.city ?? '');
      setAddressState(data?.state ?? '');
      setAddressCountry(data?.country ?? 'USA'); // default to USA
      setZip(data?.zip ?? '');
      setBillingAddress(isInitialAddress || !!data?.isPrimaryBAddress);
      setShippingAddress(isInitialAddress || !!data?.isPrimarySAddress);
    }
  }, [
    data,
    isInlineForm,
    isInitialAddress,
    isOpen,
    setAddress1,
    setAddress2,
    setAddressCategory,
    setAddressCountry,
    setAddressState,
    setAttention,
    setBillingAddress,
    setCity,
    setCompany,
    setShippingAddress,
    setZip
  ]);

  const form = useMemo(() => {
    return (
      <>
        <TextInput
          className={isInlineForm ? 'col-span-full' : ''}
          label="Attention"
          value={attention}
          onChange={setAttention}
          maxLength={44}
          acceptNumbers={false}
          acceptSpecialChars={false}
        />
        <SelectSubPanel
          buttonRef={typeRef}
          onClosePanel={handleOnClose}
          items={addressCategories}
          label="Type"
          selectedItem={addressCategory}
          onSelectedItemChange={setAddressCategory}
          required
        />
        <TextInput label="Company" value={company} onChange={setCompany} maxLength={100} />
        <TextInput
          label="Address"
          value={address1}
          onChange={setAddress1}
          maxLength={75}
          inputRef={addressRef}
          required
        />
        <TextInput label="Address 2" value={address2} onChange={setAddress2} maxLength={75} />
        <TextInput
          label="City"
          value={city}
          onChange={setCity}
          maxLength={40}
          inputRef={cityRef}
          required
        />
        <SelectSubPanel
          buttonRef={stateRef}
          onClosePanel={handleOnClose}
          items={addressStates}
          label="State"
          selectedItem={addressState}
          onSelectedItemChange={setAddressState}
          required={isUnitedStatesOrCanada}
          disabled={!!addressCountry && !isUnitedStatesOrCanada}
        />
        <TextInput
          label="ZIP/Postal code"
          value={zip}
          onChange={setZip}
          maxLength={10}
          required={isUnitedStatesOrCanada}
          acceptLetters={true}
          acceptSpaces={true}
          acceptSpecialChars={true}
          errors={isUnitedStates ? zipErrors : []}
          inputRef={zipRef}
        />
        <SelectSubPanel
          onClosePanel={handleOnClose}
          items={addressCountries}
          label="Country"
          selectedItem={addressCountry}
          onSelectedItemChange={setAddressCountry}
          required
        />
        {!isInlineForm && (
          <div className="flex flex-col gap-6">
            {type !== 'shipping' && (
              <Checkbox
                checked={billingAddress}
                disabled={isInitialAddress || data?.isPrimaryBAddress}
                onCheckedChange={toggleBillingAddress}
              >
                <Checkbox.Label>Default Billing Address </Checkbox.Label>
              </Checkbox>
            )}
            {type !== 'billing' && (
              <Checkbox
                checked={shippingAddress}
                disabled={isInitialAddress || data?.isPrimarySAddress}
                onCheckedChange={toggleShippingAddress}
              >
                <Checkbox.Label>Default Shipping Address </Checkbox.Label>
              </Checkbox>
            )}
          </div>
        )}
      </>
    );
  }, [
    isInlineForm,
    attention,
    setAttention,
    typeRef,
    handleOnClose,
    addressCategories,
    addressCategory,
    setAddressCategory,
    company,
    setCompany,
    address1,
    setAddress1,
    addressRef,
    address2,
    setAddress2,
    city,
    setCity,
    cityRef,
    stateRef,
    addressStates,
    addressState,
    setAddressState,
    isUnitedStatesOrCanada,
    addressCountry,
    zip,
    setZip,
    isUnitedStates,
    zipRef,
    addressCountries,
    setAddressCountry,
    type,
    billingAddress,
    isInitialAddress,
    data?.isPrimaryBAddress,
    data?.isPrimarySAddress,
    toggleBillingAddress,
    shippingAddress,
    toggleShippingAddress
  ]);

  return isInlineForm ? (
    <Root onSubmit={event => event.preventDefault()} className="flex flex-col gap-[32px]">
      <EditPanelProvider validationFailed={validationFailed ?? false}>
        <div className="grid grid-cols-1 gap-[32px] sm:grid-cols-2">{form}</div>
        {isInlineForm && (
          <Button
            loading={isProcessing}
            onClick={handleOnSave}
            color="black"
            label="Review order"
            className="text-1 w-full py-[25px] font-bold"
          />
        )}
      </EditPanelProvider>
    </Root>
  ) : (
    form
  );
};

export default AddressForm;
